import { useCallback, useMemo, useReducer } from 'react'
import type {
  CampaignAudience,
  CampaignAudienceFilters,
  CampaignEmailContent,
  CampaignScheduleMode,
  CampaignType,
  CreateCampaignInput,
  CreateFlowInput,
  EmailContentMode,
  FlowDelayUnit,
  FlowTriggerMetric,
  LoyaltyBrand,
  LoyaltyCampaign,
  LoyaltyFlow,
  UpdateCampaignInput,
  UpdateFlowInput,
} from '../../../../types/loyaltyMarketing'
import { normalizeCampaignType } from '../../../../types/loyaltyMarketing'
import type { LoyaltyProfile } from '../../../../types/loyaltyTracking'
import { defaultSettings, sanitizeEmailSettings } from './blocks/catalog'
import { layoutToBlocks } from './blocks/layoutToBlocks'
import type { Block, EmailDoc, TemplateKey } from './blocks/types'

// Draft state for the campaign builder (spec §2.2): every field the page used
// to hold in ~30 useStates, plus the block email document. Pure helpers below
// turn a saved campaign / flow into a draft and a draft into wire payloads.

// 'smart' = Smart send time. Hidden in the UI now, but a loaded smart
// campaign keeps it (read-only note on the Schedule step).
export type DraftScheduleMode = 'now' | 'fixed' | 'smart'
export type DraftAudienceMode = 'all' | 'segment' | 'ids'

export interface DraftState {
  name: string
  // null = nothing chosen yet (a NEW campaign starts with no type).
  type: CampaignType | null
  // Sender — blank keeps the store's SMTP default.
  fromEmail: string
  fromLabel: string
  // Inbox
  subject: string
  previewText: string
  // The template's CTA label (seeds the starter's button).
  ctaText: string
  // Legacy SMS records only; email coupons live in coupon blocks.
  couponCode: string
  smsBody: string

  // ── Audience ──
  audienceMode: DraftAudienceMode
  segmentIds: string[]
  selectedCustomers: LoyaltyProfile[]
  filters: CampaignAudienceFilters
  // Legacy random-sample audience; kept while the mode stays 'all'.
  percentage: number | null
  // The saved audience, verbatim. Re-sent unchanged until the user touches
  // the Audience step (Review Focus #1).
  rawAudience: CampaignAudience | null
  audienceTouched: boolean

  // ── Schedule ──
  scheduleMode: DraftScheduleMode
  scheduledAt: string // datetime-local value
  endsAt: string // datetime-local value
  triggerMetric: FlowTriggerMetric
  delayValue: string // hours
  delayUnit: FlowDelayUnit
  minCartValue: string
  // Days a customer can't be emailed again by this flow; '0' = no limit.
  cooldownDays: string

  // ── Content ──
  emailDoc: EmailDoc
  templateKey: TemplateKey | null
  contentMode: EmailContentMode
  // Blocks changed since the last template apply (TemplateStep confirms
  // before replacing them).
  blocksEdited: boolean
  // A legacy layout campaign converted to blocks on open.
  convertedFromLayout: boolean
}

const AUDIENCE_KEYS: ReadonlyArray<keyof DraftState> = [
  'audienceMode',
  'segmentIds',
  'selectedCustomers',
  'filters',
  'percentage',
]

export const DEFAULT_CTA_TEXT = 'Shop Now'

// Prototype defaults for a NEW abandoned-cart flow: wait 1 hour, carts over
// $20. (The 4h option stays available in the Schedule step.)
export const CART_DEFAULT_DELAY_HOURS = 1
export const CART_DEFAULT_MIN_VALUE = '20'

export const initialDraftState = (
  over: Partial<DraftState> = {},
): DraftState => ({
  name: '',
  type: null,
  fromEmail: '',
  fromLabel: '',
  subject: '',
  previewText: '',
  ctaText: DEFAULT_CTA_TEXT,
  couponCode: '',
  smsBody: '',
  audienceMode: 'all',
  segmentIds: [],
  selectedCustomers: [],
  filters: {},
  percentage: null,
  rawAudience: null,
  audienceTouched: false,
  scheduleMode: 'now',
  scheduledAt: '',
  endsAt: '',
  triggerMetric: 'abandoned_cart',
  delayValue: String(CART_DEFAULT_DELAY_HOURS),
  delayUnit: 'hours',
  minCartValue: CART_DEFAULT_MIN_VALUE,
  // New automations default to at most one email per customer every 3 days.
  cooldownDays: '3',
  emailDoc: { blocks: [], settings: defaultSettings() },
  templateKey: null,
  contentMode: 'blocks',
  blocksEdited: false,
  convertedFromLayout: false,
  ...over,
})

// ── Helpers ─────────────────────────────────────────────────────────────────

// ISO timestamp → value for a datetime-local input (local timezone).
export const toDatetimeLocalValue = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Stored (delay_value, delay_unit) → whole hours for the Wait select. Flows
// created before v2 may carry minutes/days; they round to the nearest hour.
export const delayToHours = (value: number, unit: FlowDelayUnit): number => {
  if (unit === 'days') return value * 24
  if (unit === 'minutes') return Math.max(1, Math.round(value / 60))
  return value
}

// Stored delay → the Wait select's (value, unit). Sub-hour minute delays
// (the non-production "1 minute" test option) stay in minutes so re-saving
// keeps them; everything else is whole hours.
export const delayToDraft = (
  value: number,
  unit: FlowDelayUnit,
): { delayValue: string; delayUnit: FlowDelayUnit } =>
  unit === 'minutes' && value > 0 && value < 60
    ? { delayValue: String(value), delayUnit: 'minutes' }
    : { delayValue: String(delayToHours(value, unit)), delayUnit: 'hours' }

// Audience customer_ids come back as bare ids — hydrate placeholder chips
// that keep the ids intact (the chip label falls back to the id).
const placeholderProfile = (customerId: string): LoyaltyProfile => ({
  customer_id: customerId,
  email: null,
  first_name: null,
  last_name: null,
  subscribed: null,
  is_loyalty_member: true,
})

// Every block, columns' children included (one nesting level).
export const flattenBlocks = (blocks: Block[]): Block[] =>
  (blocks || []).flatMap((b) =>
    b.type === 'columns' && Array.isArray(b.props?.cols)
      ? [b, ...flattenBlocks((b.props.cols as Block[][]).flat())]
      : [b],
  )

// The first coupon block's code — what `{{coupon}}` and the list show.
export const docCouponCode = (doc: EmailDoc): string => {
  for (const b of flattenBlocks(doc.blocks)) {
    const code = b.type === 'coupon' ? String(b.props?.code ?? '').trim() : ''
    if (code) return code
  }
  return ''
}

// Images that render (have a src) but carry no alt text.
export const missingAltCount = (doc: EmailDoc): number =>
  flattenBlocks(doc.blocks).filter(
    (b) =>
      (b.type === 'image' || b.type === 'split') &&
      String(b.props?.src ?? '').trim() &&
      !String(b.props?.alt ?? '').trim(),
  ).length

const docFromRecord = (
  r: LoyaltyCampaign | LoyaltyFlow,
  brand: LoyaltyBrand,
): Pick<
  DraftState,
  | 'emailDoc'
  | 'templateKey'
  | 'contentMode'
  | 'convertedFromLayout'
  | 'blocksEdited'
> => {
  if (r.content_mode === 'blocks' && Array.isArray(r.blocks)) {
    return {
      emailDoc: {
        blocks: r.blocks,
        settings: r.email_settings ?? defaultSettings(brand.brandColor),
      },
      templateKey: r.template_key ?? null,
      contentMode: 'blocks',
      convertedFromLayout: false,
      // Saved content counts as the user's own: a template pick confirms.
      blocksEdited: r.blocks.length > 0,
    }
  }
  // Opened in the builder → converted; it only switches to blocks mode once
  // it is saved.
  return {
    emailDoc: {
      blocks: layoutToBlocks(
        {
          subject: r.subject ?? '',
          body_html: r.body_html ?? '',
          cta_text: r.cta_text ?? '',
          cta_url: r.cta_url ?? '',
          logo_url: r.logo_url ?? null,
          coupon_code: r.coupon_code ?? '',
        },
        brand.brandLogoUrl,
      ),
      settings: defaultSettings(brand.brandColor),
    },
    templateKey: r.template_key ?? null,
    contentMode: 'blocks',
    convertedFromLayout: true,
    blocksEdited: true,
  }
}

const sharedContent = (r: LoyaltyCampaign | LoyaltyFlow) => ({
  subject: r.subject ?? '',
  previewText: r.preview_text ?? '',
  ctaText: r.cta_text || DEFAULT_CTA_TEXT,
  couponCode: r.coupon_code ?? '',
  // Not editable in the builder (managed in Settings → Ecommerce): a legacy
  // custom from_email is dropped so the next save falls back to the store sender.
  fromEmail: '',
  fromLabel: r.from_label ?? '',
  smsBody: r.sms_body ?? '',
})

export function draftFromCampaign(
  c: LoyaltyCampaign,
  brand: LoyaltyBrand,
): DraftState {
  const a = c.audience
  const segIds = a?.segment_ids?.length
    ? a.segment_ids
    : a?.segment_id
      ? [a.segment_id]
      : []
  let audience: Partial<DraftState>
  if (a?.type === 'segment' && segIds.length) {
    audience = { audienceMode: 'segment', segmentIds: [...segIds] }
  } else if (a?.type === 'ids' && a.customer_ids?.length) {
    audience = {
      audienceMode: 'ids',
      selectedCustomers: a.customer_ids.map(placeholderProfile),
    }
  } else {
    audience = {
      audienceMode: 'all',
      percentage:
        a?.type === 'percentage' && a.percentage ? a.percentage : null,
    }
  }

  // `schedule_mode` is authoritative; `scheduled_at` is the fallback for rows
  // written before it existed.
  let scheduleMode: DraftScheduleMode = 'now'
  if (c.schedule_mode === 'smart') scheduleMode = 'smart'
  else if (c.schedule_mode === 'schedule' || c.scheduled_at)
    scheduleMode = 'fixed'

  return initialDraftState({
    name: c.name ?? '',
    type: normalizeCampaignType(c.type, c.channel, c.display_type),
    ...sharedContent(c),
    ...audience,
    filters: { ...(a?.filters ?? {}) },
    rawAudience: a ? JSON.parse(JSON.stringify(a)) : null,
    audienceTouched: false,
    scheduleMode,
    scheduledAt:
      scheduleMode === 'fixed' && c.scheduled_at
        ? toDatetimeLocalValue(c.scheduled_at)
        : '',
    endsAt: c.ends_at ? toDatetimeLocalValue(c.ends_at) : '',
    ...docFromRecord(c, brand),
  })
}

export function draftFromFlow(f: LoyaltyFlow, brand: LoyaltyBrand): DraftState {
  return initialDraftState({
    name: f.name ?? '',
    type:
      f.channel === 'sms'
        ? 'sms'
        : f.trigger_metric === 'abandoned_cart'
          ? 'abandoned_cart'
          : 'email',
    ...sharedContent(f),
    triggerMetric: f.trigger_metric,
    ...delayToDraft(f.delay_value ?? 0, f.delay_unit ?? 'hours'),
    minCartValue: String(f.min_cart_value ?? 0),
    cooldownDays: String(f.resend_cooldown_days ?? 0),
    ...docFromRecord(f, brand),
  })
}

// ── Payloads ────────────────────────────────────────────────────────────────

// Any filter key counts — legacy keys the UI doesn't know are kept (state
// holds the saved filters object; the UI only overlays purchase / exclude).
const hasFilters = (f: CampaignAudienceFilters | undefined) =>
  Boolean(f && Object.keys(f).length > 0)

export const buildAudience = (state: DraftState): CampaignAudience => {
  // Untouched → the saved audience verbatim (percentage, segment_id,
  // filters… whatever it was).
  if (!state.audienceTouched && state.rawAudience)
    return JSON.parse(JSON.stringify(state.rawAudience))
  const filters = hasFilters(state.filters)
    ? { filters: { ...state.filters } }
    : {}
  if (state.audienceMode === 'segment' && state.segmentIds.length > 0)
    return { type: 'segment', segment_ids: [...state.segmentIds], ...filters }
  if (state.audienceMode === 'ids' && state.selectedCustomers.length > 0)
    return {
      type: 'ids',
      customer_ids: state.selectedCustomers.map((c) => c.customer_id),
    }
  // Never silently widen a legacy random sample to everyone.
  if (state.audienceMode === 'all' && state.percentage)
    return { type: 'percentage', percentage: state.percentage, ...filters }
  return { type: 'all', ...filters }
}

export const parseDatetime = (value: string): Date | null => {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

export const scheduleModeForApi = (
  mode: DraftScheduleMode,
): CampaignScheduleMode => (mode === 'fixed' ? 'schedule' : mode)

const isSmsState = (state: DraftState) => state.type === 'sms'

// The email content fields. On create a blank sender is OMITTED so the
// server fills it from the store's SMTP settings; on update it is sent as ''
// so the user can clear an override. `hasEmptyProductBlocks` (derived at save
// time, like compiledHtml) flags a compiled_html holding a product section
// that resolved to no products — the server refuses to send a flagged record;
// undefined omits the field (SMS / callers without a catalog).
const emailContent = (
  state: DraftState,
  compiledHtml: string,
  mode: 'create' | 'update',
  hasEmptyProductBlocks?: boolean,
): CampaignEmailContent => {
  const content: CampaignEmailContent = {
    subject: state.subject.trim(),
    preview_text: state.previewText.trim(),
    coupon_code: docCouponCode(state.emailDoc),
    content_mode: 'blocks',
    blocks: state.emailDoc.blocks,
    email_settings: sanitizeEmailSettings(state.emailDoc.settings),
    compiled_html: compiledHtml,
    template_key: state.templateKey,
    ...(hasEmptyProductBlocks === undefined
      ? {}
      : { has_empty_product_blocks: hasEmptyProductBlocks }),
  }
  if (mode === 'update' || state.fromEmail.trim())
    content.from_email = state.fromEmail.trim()
  if (mode === 'update' || state.fromLabel.trim())
    content.from_label = state.fromLabel.trim()
  return content
}

const smsContent = (state: DraftState) => ({
  sms_body: state.smsBody,
  coupon_code: state.couponCode,
})

export const effectiveName = (state: DraftState, isFlow: boolean): string =>
  state.name.trim() || (isFlow ? 'Untitled Automation' : 'Untitled Campaign')

const scheduleFields = (state: DraftState, now = new Date()) => {
  const at = parseDatetime(state.scheduledAt)
  const ends = parseDatetime(state.endsAt)
  return {
    scheduled_at:
      state.scheduleMode === 'fixed' && at && at > now
        ? at.toISOString()
        : undefined,
    ends_at: ends ? ends.toISOString() : undefined,
  }
}

export function buildPayload(
  state: DraftState,
  compiledHtml: string,
  send: CreateCampaignInput['send'] = 'draft',
  hasEmptyProductBlocks?: boolean,
): CreateCampaignInput {
  const sms = isSmsState(state)
  const { scheduled_at, ends_at } = scheduleFields(state)
  const payload: CreateCampaignInput = {
    name: effectiveName(state, false),
    type: state.type ?? 'email',
    channel: sms ? 'sms' : 'email',
    audience: buildAudience(state),
    send,
    // Always sent (drafts too) so the send-time choice survives a reopen.
    schedule_mode: scheduleModeForApi(state.scheduleMode),
    ...(sms
      ? smsContent(state)
      : emailContent(state, compiledHtml, 'create', hasEmptyProductBlocks)),
  }
  if (scheduled_at) payload.scheduled_at = scheduled_at
  if (ends_at) payload.ends_at = ends_at
  return payload
}

// PATCH body. `scheduleLocked` (a launched campaign) leaves the schedule to
// the Campaigns list.
export function buildUpdatePayload(
  state: DraftState,
  compiledHtml: string,
  opts: {
    scheduleLocked?: boolean
    endsAtValid?: boolean
    hasEmptyProductBlocks?: boolean
  } = {},
): UpdateCampaignInput {
  const sms = isSmsState(state)
  const { scheduled_at, ends_at } = scheduleFields(state)
  return {
    name: effectiveName(state, false),
    // A legacy SMS record keeps its stored type — the server rejects
    // `type: 'sms'` on PATCH.
    ...(sms || !state.type ? {} : { type: state.type }),
    audience: buildAudience(state),
    // An invalid end (e.g. already past on a launched campaign) is cleared,
    // as the old builder did.
    ends_at: opts.endsAtValid === false ? null : (ends_at ?? null),
    ...(opts.scheduleLocked
      ? {}
      : {
          schedule_mode: scheduleModeForApi(state.scheduleMode),
          ...(scheduled_at ? { scheduled_at } : {}),
        }),
    ...(sms
      ? smsContent(state)
      : emailContent(
          state,
          compiledHtml,
          'update',
          opts.hasEmptyProductBlocks,
        )),
  }
}

const flowTrigger = (state: DraftState) => {
  const delay = Number.parseInt(state.delayValue, 10)
  return {
    trigger_metric: state.triggerMetric,
    delay_value: Number.isFinite(delay) && delay >= 0 ? delay : 0,
    delay_unit: state.delayUnit,
    resend_cooldown_days: Number.parseInt(state.cooldownDays, 10) || 0,
    ...(state.triggerMetric === 'abandoned_cart'
      ? { min_cart_value: Number(state.minCartValue) || 0 }
      : {}),
  }
}

// Abandoned-cart flows are block emails too.
export function buildFlowPayload(
  state: DraftState,
  compiledHtml: string,
  status: CreateFlowInput['status'] = 'draft',
  hasEmptyProductBlocks?: boolean,
): CreateFlowInput {
  const sms = isSmsState(state)
  return {
    name: effectiveName(state, true),
    status,
    ...flowTrigger(state),
    channel: sms ? 'sms' : 'email',
    ...(sms
      ? smsContent(state)
      : emailContent(state, compiledHtml, 'create', hasEmptyProductBlocks)),
  }
}

export function buildFlowUpdatePayload(
  state: DraftState,
  compiledHtml: string,
  status?: UpdateFlowInput['status'],
  hasEmptyProductBlocks?: boolean,
): UpdateFlowInput {
  const sms = isSmsState(state)
  return {
    name: effectiveName(state, true),
    ...flowTrigger(state),
    ...(status ? { status } : {}),
    ...(sms
      ? smsContent(state)
      : emailContent(state, compiledHtml, 'update', hasEmptyProductBlocks)),
  }
}

// Which API a save goes to. An existing record keeps its own kind (a
// campaign of type abandoned_cart is still a campaign); only a NEW record of
// type abandoned_cart becomes a flow.
export type RecordKind = 'campaign' | 'flow'

export const resolveRecordKind = (p: {
  isEditingCampaign: boolean
  isEditingFlow: boolean
  savedKind: RecordKind | null
  type: CampaignType | null
}): RecordKind => {
  if (p.isEditingFlow) return 'flow'
  if (p.isEditingCampaign) return 'campaign'
  if (p.savedKind) return p.savedKind
  return p.type === 'abandoned_cart' ? 'flow' : 'campaign'
}

// The block-email fields a duplicate carries over (null/absent omitted — the
// create serializers don't accept null for them).
export const blocksCopyFields = (
  r: LoyaltyCampaign | LoyaltyFlow,
): Partial<CampaignEmailContent> => {
  if (r.content_mode !== 'blocks') return {}
  return {
    content_mode: 'blocks',
    ...(r.blocks ? { blocks: r.blocks } : {}),
    ...(r.email_settings
      ? { email_settings: sanitizeEmailSettings(r.email_settings) }
      : {}),
    ...(r.compiled_html != null ? { compiled_html: r.compiled_html } : {}),
    ...(r.template_key ? { template_key: r.template_key } : {}),
    // A duplicate of a flagged record is just as unsendable as the original.
    ...(typeof r.has_empty_product_blocks === 'boolean'
      ? { has_empty_product_blocks: r.has_empty_product_blocks }
      : {}),
  }
}

// ── Hook ────────────────────────────────────────────────────────────────────

type Action =
  | { kind: 'set'; key: keyof DraftState; value: unknown }
  | { kind: 'patch'; value: Partial<DraftState> }
  | { kind: 'doc'; doc: EmailDoc }
  | { kind: 'reset'; state: DraftState }

const reducer = (state: DraftState, action: Action): DraftState => {
  switch (action.kind) {
    case 'set': {
      if (Object.is(state[action.key], action.value)) return state
      const next = { ...state, [action.key]: action.value }
      // Touching anything on the Audience step switches the payload from the
      // saved audience to the UI's.
      if (AUDIENCE_KEYS.includes(action.key)) next.audienceTouched = true
      return next
    }
    case 'patch': {
      const next = { ...state, ...action.value }
      if (
        Object.keys(action.value).some((k) =>
          AUDIENCE_KEYS.includes(k as keyof DraftState),
        )
      )
        next.audienceTouched = action.value.audienceTouched ?? true
      return next
    }
    case 'doc':
      return { ...state, emailDoc: action.doc, blocksEdited: true }
    case 'reset':
      return action.state
    default:
      return state
  }
}

export interface CampaignDraft {
  state: DraftState
  set<K extends keyof DraftState>(k: K, v: DraftState[K]): void
  // Several fields at once (template apply, type switch).
  patch(p: Partial<DraftState>): void
  // Editor changes — marks the blocks as edited.
  setDoc(doc: EmailDoc): void
  reset(s: DraftState): void
}

export function useCampaignDraft(initial?: Partial<DraftState>): CampaignDraft {
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    initialDraftState(initial),
  )
  const set = useCallback(
    <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
      dispatch({ kind: 'set', key, value }),
    [],
  )
  const patch = useCallback(
    (value: Partial<DraftState>) => dispatch({ kind: 'patch', value }),
    [],
  )
  const setDoc = useCallback(
    (doc: EmailDoc) => dispatch({ kind: 'doc', doc }),
    [],
  )
  const reset = useCallback(
    (s: DraftState) => dispatch({ kind: 'reset', state: s }),
    [],
  )
  return useMemo(
    () => ({ state, set, patch, setDoc, reset }),
    [state, set, patch, setDoc, reset],
  )
}
